import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'design-system/tooltip/tooltip';
import { cn } from 'lib/cn';
import {
  type AriaAttributes,
  cloneElement,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react';

export type SidebarNavIcon = ComponentType<{
  stroke?: number | string;
  className?: string;
}>;

const sidebarNavIconClass = (isActive: boolean) =>
  cn(
    'size-4 shrink-0 transition-colors',
    isActive
      ? 'text-foreground'
      : 'text-foreground-tertiary group-hover/nav-link:text-foreground',
  );

const itemClass = (isActive: boolean) =>
  cn(
    'group/nav-link text-foreground-secondary focus-ring text-headline flex h-8 w-full cursor-default items-center gap-2 overflow-hidden rounded-md px-3 text-left transition-colors',
    isActive
      ? 'bg-fill-quaternary text-foreground'
      : 'hover:bg-fill-quaternary hover:text-foreground',
  );

interface RenderElementProps extends AriaAttributes {
  className?: string;
}

interface SidebarNavItemProps {
  label: string;
  /** The interactive element carrying the item's semantics: a Link, anchor, or button. */
  render: ReactElement<RenderElementProps>;
  collapsed?: boolean;
  isActive?: boolean;
  icon?: SidebarNavIcon;
  /** Custom leading visual replacing the icon slot, such as an avatar. */
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Tooltip content while collapsed; defaults to the label. */
  tooltip?: ReactNode;
  className?: string;
}

// One element shape for both states so toggling collapse transitions instead of remounting.
// Tooltip disabled rather than unmounted: with no popup mounted, hovered triggers never close and all fire at once when one appears.
export const SidebarNavItem = ({
  label,
  render,
  collapsed = false,
  isActive = false,
  icon: Icon,
  leading,
  trailing,
  tooltip,
  className,
}: SidebarNavItemProps) => {
  const item = cloneElement(
    render,
    {
      'aria-current': isActive ? 'page' : undefined,
      className: cn(itemClass(isActive), render.props.className, className),
    },
    leading ??
      (Icon ? (
        <Icon stroke={1.5} className={sidebarNavIconClass(isActive)} />
      ) : null),
    <span
      className={cn(
        'min-w-0 flex-1 truncate transition-opacity',
        collapsed && 'opacity-0',
      )}
    >
      {label}
    </span>,
    trailing,
  );

  return (
    <Tooltip disabled={!collapsed}>
      <TooltipTrigger render={<div>{item}</div>} />
      <TooltipContent side="right">{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
};
SidebarNavItem.displayName = 'SidebarNavItem';
